"use client";

import { useRef } from "react";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import CryptoPillar from "@/components/global/ui/CryptoPillar";
import { CTAButton } from "@/shadcn/ui/button";
import { motion, useScroll, useTransform } from "framer-motion";

import bg from "@/public/global/bg-art.jpg";
import knight from "@/public/global/knight.png";
import monuent from "@/public/text-only/monuent.png";
import { PageTextOnlyLPOldDesignSchema } from "@/sanity/pages/PageTextOnlyLPOldDesignTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import { Carousel, CarouselContent, CarouselItem } from "@/shadcn/ui/carousel";
import AutoScroll from "embla-carousel-auto-scroll";

export default function HeroSection({
  data,
  className,
  showTopBanner,
}: {
  data: PageTextOnlyLPOldDesignSchema;
  className?: string;
  showTopBanner?: boolean;
}) {
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ["start end", "end start"],
  });

  const yEthereum = useTransform(scrollYProgress, [0, 1], ["-3%", "3%"]);
  const ySolana = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const yBitcoin = useTransform(scrollYProgress, [0, 1], ["5%", "-5%"]);
  const yKnight = useTransform(scrollYProgress, [0, 1], ["-2%", "2%"]);

  return (
    <section
      id="hero-section"
      ref={parallaxRef}
      className={cn(
        "relative overflow-clip px-[2rem] lg:px-[5rem] pt-[2rem] lg:pt-[5rem]",
        showTopBanner && "mt-[16rem] lg:mt-[7rem]",
        className,
      )}>
      <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem] pt-[13rem] lg:pt-[18.75rem] pb-[40rem] lg:pb-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#F6F9F7] to-[#E6F2EB]">
          <Image
            className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,90rem)] !max-w-none !h-full object-cover object-top mix-blend-luminosity opacity-[.10]"
            src={bg}
            quality={100}
            alt="background art"
          />
        </div>

        <div className="container flex relative z-10 flex-col items-center px-[4.62rem]">
          {data?.heroChip && <ChipSerif text={data?.heroChip} />}

          {data?.heroTitle && (
            <div className="richtext prose-h1:text-[4rem] lg:prose-h1:max-w-[97.375rem] lg:prose-h1:text-[7rem] prose-h6:text-[1.75rem] prose-h6:text-black/60 prose-h6:font-normal lg:prose-h6:max-w-[49.375rem] prose-p:lg:max-w-[59.125rem]">
              <PortableText
                value={data.heroTitle}
                components={richTextComponent}
              />
            </div>
          )}

          {data?.heroCTA && (
            <CTAButton
              className="mt-[3rem]"
              data={data.heroCTA}
            />
          )}

          <div className="hidden relative z-[5] mb-[45rem] lg:mb-[4.75rem] mt-[3.5rem] lg:mt-[18rem] w-[min(148.75rem,100%)] lg:flex flex-wrap lg:flex-nowrap lg:flex-row gap-[2rem] lg:gap-[3rem] justify-center lg:justify-between">
            {data.pills &&
              data.pills.map((item, index) => (
                <div
                  key={"heropill" + index}
                  className="font-medium rounded-full border w-fit bg-[#E4EDE8] border-[#D5E2DB] text-[#103037] text-[1.75rem] px-[2.25rem] py-[1rem] shrink-0 font-geist lg:text-[2rem]">
                  {item}
                </div>
              ))}
          </div>
        </div>

        <div className="w-full lg:hidden mt-[4.65rem]">
          <Carousel
            opts={{
              loop: true,
            }}
            plugins={[AutoScroll({ speed: 1 })]}
            className="w-full">
            <CarouselContent className="w-full">
              {data.pills &&
                data.pills.map((item, index) => (
                  <CarouselItem
                    key={index}
                    className="px-[1rem]">
                    <div
                      key={"heropill" + index}
                      className="font-medium rounded-full border w-fit bg-[#E4EDE8] border-[#D5E2DB] text-[#103037] text-[1.75rem] px-[2.25rem] py-[1rem] shrink-0 lg:text-[2rem]">
                      {item}
                    </div>
                  </CarouselItem>
                ))}
            </CarouselContent>
          </Carousel>
        </div>
        <motion.div
          className="absolute bottom-0 right-[-15rem] w-[min(46.5rem,100%)] lg:w-[min(72rem,100%)]  z-0 "
          style={{ y: yKnight }}>
          <Image
            className="!w-full -scale-x-100"
            src={knight}
            quality={100}
            alt="archer"
          />
        </motion.div>
        <motion.div
          className="absolute bottom-0 left-0 w-[min(38.5rem,100%)] lg:w-[min(72rem,100%)]  z-0 "
          style={{ y: yKnight }}>
          <Image
            className="!w-full"
            src={monuent}
            quality={100}
            alt="atlas"
          />
        </motion.div>

        <div className="absolute bottom-0 left-0 w-full bg-gradient-to-b from-transparent backdrop-blur-[2px] to-[#F4F8F5]/90 h-[10rem] lg:h-[20rem]"></div>
      </div>
    </section>
  );
}
